import { useState } from 'react';

export type MoscowCategory = 'Must' | 'Should' | 'Could' | 'Wont';

export interface MoscowLiveItem {
  id: string;
  name: string;
  description?: string;
  category: MoscowCategory | null;
}

const BUCKETS: { key: MoscowCategory; label: string; hint: string; labelClass: string; tagClass: string }[] = [
  { key: 'Must', label: 'Must have', hint: 'Non-negotiable', labelClass: 'text-luminous-coral-700', tagClass: 'text-luminous-coral-700/90 border-luminous-coral-700/30 bg-luminous-coral-700/7' },
  { key: 'Should', label: 'Should have', hint: 'Important', labelClass: 'text-electric-mustard-600', tagClass: 'text-electric-mustard-600/90 border-electric-mustard-600/30 bg-electric-mustard-600/7' },
  { key: 'Could', label: 'Could have', hint: 'If time allows', labelClass: 'text-[var(--text-sec)]', tagClass: 'text-[var(--text-sec)] border-[var(--rule-bright)] bg-[var(--bg-raised)]/[0.03]' },
  { key: 'Wont', label: "Won't have", hint: 'Out of scope', labelClass: 'text-[var(--text-ter)]', tagClass: 'text-[var(--text-ter)] border-[var(--rule)]' },
];

interface MoscowLiveProps {
  items: MoscowLiveItem[];
  onAssign: (itemId: string, category: MoscowCategory) => void;
}

export function MoscowLive({ items, onAssign }: MoscowLiveProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const uncategorized = items.filter(i => i.category == null);
  const byCat = (c: MoscowCategory) => items.filter(i => i.category === c);

  return (
    <div>
      {uncategorized.length > 0 && (
        <>
          <div className="font-heading text-[0.48rem] tracking-[0.1em] uppercase text-[var(--text-dim)] mb-2">
            Unassigned — select an item, then choose a tier
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {uncategorized.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedId(item.id === selectedId ? null : item.id)}
                className={`text-left  border px-3 py-2 transition-colors ${
                  selectedId === item.id
                    ? 'border-electric-mustard-600/50 bg-electric-mustard-600/10'
                    : 'border-[var(--rule-bright)] bg-[var(--surface)] hover:border-[var(--slate)]'
                }`}
              >
                <div className="font-body text-[0.8rem] font-medium text-[var(--text-sec)]">{item.name}</div>
                {item.description && (
                  <div className="font-body text-[0.68rem] text-[var(--text-ter)] line-clamp-2 max-w-[200px]">{item.description}</div>
                )}
              </button>
            ))}
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-2">
        {BUCKETS.map(bucket => (
          <div
            key={bucket.key}
            className="border border-[var(--rule)] bg-[var(--surface)] p-3 min-h-[100px]"
          >
            <div className={`font-heading text-[0.54rem] tracking-[0.12em] uppercase mb-0.5 ${bucket.labelClass}`}>
              {bucket.label}
            </div>
            <div className="font-body text-[0.65rem] text-[var(--text-ter)] mb-2">{bucket.hint}</div>
            <div className="flex flex-col gap-2">
              {selectedId && (
                <button
                  type="button"
                  onClick={() => {
                    onAssign(selectedId, bucket.key);
                    setSelectedId(null);
                  }}
                  className="w-full text-left font-heading text-[0.5rem] tracking-[0.06em] uppercase text-alpine-green-400 border border-dashed border-alpine-green-500/35 rounded py-1.5 px-2 hover:bg-alpine-green-500/10"
                >
                  + Place selected here
                </button>
              )}
              <div className="flex flex-wrap gap-1">
                {byCat(bucket.key).map(item => (
                  <span key={item.id} className={`font-body text-[0.72rem] px-2 py-0.5 rounded border ${bucket.tagClass}`}>
                    {item.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
